import { PackageT } from '@/modules/nodesCanvasModule/components/storeClasses/packageT';
import { LoadRobotPackagesStrategy } from './loadRobotPackages';
import {
  Message,
  MessageField
} from '@/modules/nodesCanvasModule/components/storeClasses/message';
import { Service } from '@/modules/nodesCanvasModule/components/storeClasses/service';
import { v4 } from 'uuid';
import { Topic } from '@/modules/nodesCanvasModule/components/storeClasses/topic';
/**
Note: ros2 interface list - lists all topics and services
ros2 interface show [msg] - shows a message definition


TODO: load package messages
create an object that stores all the message results of a package
and builds the package once all the message results have been obtained.

Note: Package message dependencies: if a message is from a different package,
it will need to include a slash

Solution to dependencies in other packages: when adding message details to packagecreator, 
check if message includes params from external packages, and if so, add that package creation messages to
the flow.
Problem: circular dependencies
alternative solution
*/

export interface InterfaceDefinition {
  nameString: string;
  definition: string;
  externalPkgs?: Array<string>;
}

export class PackageCreator {
  targetPackage: PackageT;
  strategy: LoadRobotPackagesStrategy;

  topicsToLoad: Array<{ topic: string; type: string }> = [];
  // interfaces to load
  msgsDefinitions: Array<InterfaceDefinition> = [];
  srvDefinitions: Array<InterfaceDefinition> = [];

  externalMsgDependencies: Array<InterfaceDefinition> = []; // msg name (and package) of messages that are external to this package
  externalSrvDependencies: Array<InterfaceDefinition> = [];
  constructor(pkg: PackageT, strategy: LoadRobotPackagesStrategy) {
    this.targetPackage = pkg;
    this.strategy = strategy;
  }

  /**
   *
   * @param nameString - message definition as shown from ros2 interface package [packageName]
   * @param definition message definition from ROS2 interface show
   */
  addMessagedefinition(nameString: string, definition: string) {
    this.msgsDefinitions.push({
      nameString: nameString,
      definition: definition
    });
  }
  /**
   * Returns a list of package names that this package is dependend on
   * note: only works once the package has been loaded, as relies
   * on external msg dependencies which only come into effect once
   * package has been loaded
   */
  getExternalPackageDependencies(): string[] {
    let externalPackageDependencies: string[] = [];
    this.externalMsgDependencies
      .concat(this.externalSrvDependencies)
      .forEach((interfaceDef: InterfaceDefinition) => {
        if (interfaceDef.externalPkgs) {
          interfaceDef.externalPkgs.forEach((pkgName) => {
            if (!externalPackageDependencies.includes(pkgName))
              externalPackageDependencies.push(pkgName);
          });
        } else {
          externalPackageDependencies.concat(
            this.getMessageDependencies(interfaceDef).filter(
              (dep) => !externalPackageDependencies.includes(dep)
            )
          );
        }
      });
    return externalPackageDependencies;
  }

  loadPackage() {
    // sort message list by dependencies
    this.msgsDefinitions = dependencySortMessages(this.msgsDefinitions);
    // loading messages
    // continues looping until no new messages are added
    let prevMessagesLength = -1;
    while (prevMessagesLength !== this.targetPackage.msgs.length) {
      prevMessagesLength = this.targetPackage.msgs.length;
      for (let message of this.msgsDefinitions) {
        // checks if message already exists before adding
        if (this.strategy.getMessageFromNameString(message.nameString)) {
          continue;
        }

        // checking if the message is dependent on external packages
        let newMessage = this.getMessageObject(message);
        if (
          !newMessage &&
          !this.externalMsgDependencies.find(
            (msgDep) => msgDep.nameString === message.nameString
          )
        ) {
          this.externalMsgDependencies.push(message);
        } else if (newMessage) {
          this.targetPackage.msgs.push(newMessage);
          this.externalMsgDependencies = this.externalMsgDependencies.filter(
            (msg) => msg.nameString !== message.nameString
          );
          this.msgsDefinitions = this.msgsDefinitions.filter(
            (msg) => msg.nameString !== message.nameString
          );
        }
      }
    }

    // loading services
    // only need to run once as only dependent on messages
    for (let serviceDef of this.srvDefinitions) {
      if (this.strategy.getServiceFromName(serviceDef.nameString)) continue;
      let newSrv = this.getServiceObject(serviceDef);
      if (!newSrv) {
        this.externalSrvDependencies.push(serviceDef);
      } else {
        this.targetPackage.services.push(newSrv);
        this.externalSrvDependencies = this.externalSrvDependencies.filter(
          (srv) => srv.nameString !== serviceDef.nameString
        );
        this.srvDefinitions = this.srvDefinitions.filter(
          (srv) => srv.nameString !== serviceDef.nameString
        );
      }
    }

    // loading topics
    for (let topic of this.topicsToLoad) {
      if (this.strategy.getTopicFromName(topic.topic)) continue;
      let topicMsg = this.strategy.getMessageFromName(
        topic.type.split('/').reverse()[0]
      );
      if (topicMsg) {
        let newTopic = new Topic(v4(), 0, 0, topic.topic);
        newTopic.msgType = topicMsg;
        newTopic.packageId = this.targetPackage.id;
        this.targetPackage.topics.push(newTopic);
      }
    }
  }

  /**
   * Turns a message string into a message object.
   * Returns undefined if their are unmet dependencies in the fields
   * of the message. Adds the external packages to the external packages
   * array of the message definition
   * @param messageDefinition
   * @returns
   */
  getMessageObject(
    messageDefinition: InterfaceDefinition
  ): Message | undefined {
    messageDefinition.externalPkgs = [];
    let messageName = messageDefinition.nameString;
    let pkgName = messageDefinition.nameString.split('/')[0];
    if (messageName.includes('/')) {
      messageName = messageName.split('/').reverse()[0];
      if (pkgName !== this.targetPackage.name) {
        return undefined;
      }
    }
    let newMessage = new Message(messageName, messageDefinition.nameString);
    for (let param of messageDefinition.definition.split('\n')) {
      if (
        param.trim().length === 0 ||
        param.trim()[0] === '#' ||
        param.startsWith('\t') ||
        param.startsWith('    ')
      ) {
        continue;
      }
      let newField = this.getMessageFieldFromString(param);
      if (newField) {
        newMessage.fields.push(newField);
      } else if (
        param.includes('/') &&
        !messageDefinition.externalPkgs.includes(param.split('/')[0])
      ) {
        messageDefinition.externalPkgs.push(param.split('/')[0]);
      }
    }
    if (
      messageDefinition.externalPkgs &&
      messageDefinition.externalPkgs.length > 0
    ) {
      return undefined;
    }
    return newMessage;
  }

  getServiceObject(srvDefinition: InterfaceDefinition) {
    let srvName = srvDefinition.nameString;
    let pkgName = srvDefinition.nameString.split('/')[0];
    let srvPackage = this.targetPackage.id;
    if (srvName.includes('/')) {
      srvName = srvName.split('/').reverse()[0];
      let externalPkg = this.strategy.getPackageFromName(pkgName);
      if (pkgName !== this.targetPackage.name && externalPkg) {
        srvPackage = externalPkg.id;
      } else if (pkgName !== this.targetPackage.name) {
        return undefined;
      }
    }
    let newSrv = new Service(v4(), 0, 0, srvName);
    srvPackage && (newSrv.packageId = srvPackage);
    let requests = true;
    for (let param of srvDefinition.definition.split('\n')) {
      if (
        param.trim().length === 0 ||
        param.trim()[0] === '#' ||
        param.startsWith('\t') ||
        param.startsWith('    ')
      ) {
        continue;
      }
      if (param.trim() === '---') {
        requests = false;
        continue;
      }
      let newField = this.getMessageFieldFromString(param);
      if (newField) {
        if (requests) newSrv.requestList.push(newField);
        else newSrv.responseList.push(newField);
      } else if (
        !srvDefinition.externalPkgs ||
        !srvDefinition.externalPkgs.includes(param.split('/')[0])
      ) {
        if (!srvDefinition.externalPkgs) srvDefinition.externalPkgs = [];
        srvDefinition.externalPkgs.push(param.split('/')[0]);
      }
    }
    if (srvDefinition.externalPkgs && srvDefinition.externalPkgs.length > 0) {
      this.externalSrvDependencies.push(srvDefinition);
      return undefined;
    }
    return newSrv;
  }

  getMessageFieldFromString(param: string): MessageField | undefined {
    // check if external message param
    param = param.trim();
    param = param.replaceAll(/[ \t]+/g, ' ');
    let paramType = param.split(' ')[0];
    const fullParamType = param.split(' ')[0];
    let paramName = param.split(' ')[1];
    const fullParamName = param.slice(param.indexOf(' '));
    let defaultValue = undefined;
    let constantValue = undefined;
    let isArray = false;
    let typeBounds = undefined;
    let arrayBounds = undefined;
    let arrayBoundsIsLength = undefined;

    // Checking for type array
    if (paramType.includes('[')) {
      paramType = paramType.slice(0, paramType.indexOf('['));
      isArray = true;

      // check for array length or bounds
      if (
        /^[0-9]*$/.test(fullParamType.at(fullParamType.indexOf('[') + 1) || '')
      ) {
        arrayBoundsIsLength = true;
        arrayBounds = parseInt(
          fullParamType.slice(
            fullParamType.indexOf('[') + 1,
            fullParamType.indexOf(']')
          )
        );
      } else if (fullParamType.at(fullParamType.indexOf('[') + 1) === '<') {
        arrayBounds = parseInt(
          fullParamType.slice(
            fullParamType.indexOf('=') + 1,
            fullParamType.indexOf(']')
          )
        );
      }
    }

    // check for string bounds
    if (paramType.includes('<')) {
      paramType = paramType.slice(0, paramType.indexOf('<'));
      let finalIndex = fullParamType.indexOf(' ');
      if (fullParamType.includes('[')) finalIndex = fullParamType.indexOf('[');
      typeBounds = parseInt(
        fullParamType.slice(fullParamType.indexOf('=') + 1),
        finalIndex
      );
    }

    // check for constant value (note: if const, not default)
    if (fullParamName.lastIndexOf('=') > fullParamName.indexOf(' ')) {
      constantValue = fullParamName.slice(fullParamName.lastIndexOf('=') + 1);
    }
    // check for default value
    else if (fullParamName.trim().includes(' ')) {
      defaultValue = fullParamName.slice(fullParamName.indexOf(' '));
    }
    // check if message generated
    let targetMessage: string | undefined = this.targetPackage.msgs.find(
      (msg) => {
        return msg.name === paramType;
      }
    )?.id;
    // check if external message
    if (!targetMessage) {
      let targetMsgName = paramType;
      if (targetMsgName.includes('/')) {
        targetMsgName = targetMsgName.split('/')[1];
      }
      targetMessage = this.strategy.getMessageFromName(targetMsgName);
    }
    if (!targetMessage) {
      return undefined;
    }
    return new MessageField(
      targetMessage,
      paramName,
      defaultValue,
      constantValue,
      isArray,
      typeBounds,
      arrayBounds,
      arrayBoundsIsLength
    );
  }

  getMessageName(messageDefinition: InterfaceDefinition | string) {
    if (typeof messageDefinition === 'string')
      return messageDefinition.split('/').reverse()[0];
    return messageDefinition.nameString.split('/').reverse()[0];
  }

  getMessagePackage(messageDefinition: InterfaceDefinition | string) {
    if (typeof messageDefinition === 'string')
      return messageDefinition.split('/')[0];
    return messageDefinition.nameString.split('/')[0];
  }

  /**
   * Gets all the external package dependencies of the message
   * @param messageDefinition - message object or definition string
   */
  getMessageDependencies(
    messageDefinition: InterfaceDefinition | string
  ): Array<string> {
    let messageDef: string = '';
    let dependencyArray = [];
    if (typeof messageDefinition !== 'string') {
      messageDef = messageDefinition.definition;
    } else {
      messageDef = messageDefinition;
    }
    for (let param of messageDef.split('\n')) {
      let paramType = param.split(' ')[0];
      if (paramType.includes('/')) {
        dependencyArray.push(paramType.split('/')[0]);
      }
    }
    return dependencyArray;
  }
}

// function to sort a list of messages by their dependencies
const dependencySortMessages = (
  messages: Array<InterfaceDefinition>
): Array<InterfaceDefinition> => {
  // build dfs array object
  let dfsHelper: Record<
    string,
    {
      visited: boolean;
      def: InterfaceDefinition;
      adjacencies: Array<string>;
      externalDependency: boolean;
    }
  > = {};

  // setting up the helper array for sorting
  for (const message of messages) {
    let adjacencies: Array<string> = [];
    let externalDep = false;
    if (!message.definition) {
      console.warn('UNDEFINED MESSAGE');
      console.log(message);
      message.definition = '';
    }
    for (let paramString of message.definition.split('\n')) {
      // formatting the parameter string to obtian the message type
      paramString = paramString.trim();
      if (paramString.length === 0 || paramString[0] === '#') continue;
      let paramName = paramString.split(' ')[0];
      if (paramName.includes('['))
        paramName = paramName.slice(0, paramString.indexOf('['));
      if (paramName.includes('<'))
        paramName = paramName.slice(0, paramString.indexOf('<'));

      // searching for external or internal dependencies
      if (paramName.includes('/')) externalDep = true;
      else {
        let msgDep = messages.find((msg) => {
          return msg.nameString.split('/').reverse()[0] === paramName;
        });
        msgDep && adjacencies.push(msgDep.nameString);
      }
    }
    dfsHelper[message.nameString] = {
      visited: false,
      def: message,
      adjacencies: adjacencies,
      externalDependency: externalDep
    };
  }

  // sorting the array
  let stack: Array<InterfaceDefinition> = [];
  for (let node of messages) {
    if (!dfsHelper[node.nameString].visited) {
      DFS(node, dfsHelper, stack);
    }
  }
  return stack;
};

// helper function for sorting the messages with dependencies
const DFS = (
  node: InterfaceDefinition,
  helper: Record<
    string,
    {
      visited: boolean;
      def: InterfaceDefinition;
      adjacencies: Array<string>;
      externalDependency: boolean;
    }
  >,
  stack: Array<InterfaceDefinition>
) => {
  helper[node.nameString].visited = true;
  for (let neighbour of helper[node.nameString].adjacencies) {
    if (!helper[neighbour].visited) {
      DFS(helper[neighbour].def, helper, stack);
    }
  }
  stack.push(node);
};
